import { useEffect, useState } from 'react';

// Bannière d'installation PWA — l'app est déjà installable (manifest +
// service worker en place), ce composant rend juste l'invite visible et
// actionnable :
//  - Android/Chrome : capture l'événement natif `beforeinstallprompt` et
//    propose un vrai bouton "Installer" qui déclenche la boîte de dialogue
//    du navigateur.
//  - iOS/Safari : ne supporte pas cet événement (Apple ne l'implémente
//    pas), donc on affiche les 2 étapes manuelles à la place.
// Reste discrète : ne s'affiche jamais si l'app tourne déjà en mode
// installé (standalone), et se souvient d'un "plus tard" pendant 14 jours.

const DISMISS_KEY = 'cp_install_dismissed_at';
const DISMISS_DAYS = 14;

function isStandalone(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari expose ce flag non-standard au lieu de display-mode.
  if ((window.navigator as unknown as { standalone?: boolean }).standalone) return true;
  return false;
}

function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function wasDismissedRecently(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const days = (Date.now() - Number(raw)) / 86400000;
  return days < DISMISS_DAYS;
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    if (isIOS()) {
      // Pas d'événement natif sur iOS : on affiche directement l'invite
      // avec les instructions manuelles.
      setShowIOSHint(true);
      return;
    }

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  }

  async function install() {
    if (!deferred) return;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted' || outcome === 'dismissed') {
      setDeferred(null);
      dismiss();
    }
  }

  if (dismissed || (!deferred && !showIOSHint)) return null;

  return (
    <div className="install-banner">
      <div className="install-banner-icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/>
        </svg>
      </div>
      <div className="install-banner-text">
        <strong>Installer CaissePilot</strong>
        {showIOSHint ? (
          <span>Appuyez sur <strong>Partager</strong> (icône ⬆︎ en bas de Safari) puis <strong>« Sur l'écran d'accueil »</strong>.</span>
        ) : (
          <span>Ajoutez l'app à votre écran d'accueil pour un accès rapide, même hors ligne.</span>
        )}
      </div>
      <div className="install-banner-actions">
        {!showIOSHint && <button className="primary small" onClick={install}>Installer</button>}
        <button className="small" onClick={dismiss} aria-label="Fermer">✕</button>
      </div>
    </div>
  );
}
